import * as React from 'react';
import {
  Body,
  Loading,
  SystemMessage,
} from 'vet-bones/design-system/components';

import { TEST_IDS } from 'src/constants/testIds';

import styles from 'src/components/resourceDialog/styles/resourcePreviewStates.module.scss';

/**
 * Loading state for resource previews.
 * Displays a centered loading spinner.
 */
export const PreviewLoadingState: React.VFC = () => (
  <div className={styles.previewLoading}>
    <Loading size="lg" />
  </div>
);

export type PreviewErrorStateProps = {
  /** The error title to display */
  title: string;
  /** Optional error message */
  message?: string;
  /** Optional array of row-specific errors (for CSV parsing errors) */
  errors?: Array<{ row: number; message: string }>;
};

export const PreviewErrorState: React.VFC<PreviewErrorStateProps> = ({
  title,
  message,
  errors,
}) => (
  <SystemMessage
    text={
      <>
        <Body size="xl">{title}</Body>
        {message ? (
          <Body size="md" testId={TEST_IDS.resourcePreview.errorMessage}>
            {message}
          </Body>
        ) : null}
        {errors?.map((error) => (
          <div key={`error-${error.row}-${error.message}`}>
            Row {error.row}: {error.message}
          </div>
        ))}
      </>
    }
    variant="alert"
  />
);

export type PreviewEmptyStateProps = {
  /** The message to display when there's no data */
  message: string;
};

/**
 * Empty state for resource previews.
 * Displays an informational message when there's no data to show.
 */
export const PreviewEmptyState: React.VFC<PreviewEmptyStateProps> = ({
  message,
}) => <SystemMessage text={message} variant="text" />;

export type PreviewValidationErrorStateProps = {
  /** The validation error message */
  message: string;
};

/**
 * Validation error state for resource previews.
 * Displays an alert message for validation failures (e.g., missing required options).
 */
export const PreviewValidationErrorState: React.VFC<
  PreviewValidationErrorStateProps
> = ({ message }) => <SystemMessage text={message} variant="alert" />;

export type PreviewNoSelectionStateProps = {
  /** The main title to display */
  title: string;
  /** The subtitle/description to display */
  subtitle: string;
};

/**
 * No selection state for resource previews.
 * Displays a centered message when no resource is selected (e.g., no table selected).
 */
export const PreviewNoSelectionState: React.VFC<
  PreviewNoSelectionStateProps
> = ({ title, subtitle }) => (
  <div className={styles.noSelectionContainer}>
    <Body size="2xl">{title}</Body>
    <Body className={styles.noSelectionSubtitle} size="lg" weight="light">
      {subtitle}
    </Body>
  </div>
);
